import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BudgetCard } from './budget-card';
import { BudgetTable } from './budget-table';
import { BudgetProgressBar } from './budget-progress-bar';
import { FileText, CheckCircle2, Circle, Clock } from 'lucide-react';
import { formatCurrency, formatPercent, formatHours } from '@/lib/budget-utils';
import type { SalesBudget } from '@/types/budget.types';
import type { ProjectType } from '@/types';

interface SalesBudgetViewProps {
  data: SalesBudget;
  projectType: ProjectType;
}

export function SalesBudgetView({ data, projectType }: SalesBudgetViewProps) {
  if (!data) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">No sales budget data available</p>
      </div>
    );
  }

  if (projectType === 'fixed-price') {
    return <FixedPriceSalesView data={data} />;
  } else if (projectType === 'time-and-materials') {
    return <TimeAndMaterialsSalesView data={data} />;
  } else if (projectType === 'retainer') {
    return <RetainerSalesView data={data} />;
  }

  return <div>Unknown project type</div>;
}

// Fixed Price Project View
function FixedPriceSalesView({ data }: { data: SalesBudget }) {
  const { contractValue, invoiced, recognised, remaining, milestones, revenueRecognition, phases } = data;

  if (!contractValue || !milestones || !revenueRecognition || !phases) {
    return <div>Incomplete data for fixed price project</div>;
  }

  // Create a contract value object for display
  const contractValueDisplay = {
    total: contractValue,
    invoiced: invoiced || 0,
    recognised: recognised || 0,
    remaining: remaining || 0,
  };

  // Milestone status icon
  const getMilestoneIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'invoiced':
        return <Clock className="h-4 w-4 text-blue-600" />;
      case 'due':
        return <FileText className="h-4 w-4 text-amber-600" />;
      default:
        return <Circle className="h-4 w-4 text-gray-400" />;
    }
  };

  const milestoneRows = milestones.map((milestone) => ({
    id: milestone.id,
    milestone: milestone.name,
    amount: formatCurrency(milestone.amount),
    dueDate: new Date(milestone.dueDate).toLocaleDateString(),
    invoiced: milestone.invoiced ? '✓ Yes' : '○ No',
    paid: milestone.paid ? '✓ Yes' : '○ No',
    status: (
      <div className="flex items-center gap-2">
        {getMilestoneIcon(milestone.status)}
        <span className="capitalize">{milestone.status}</span>
      </div>
    ),
  }));

  const phaseRows = phases.map((phase) => ({
    id: phase.id,
    phase: phase.name,
    allocated: formatCurrency(phase.allocated),
    recognised: formatCurrency(phase.recognised),
    invoiced: formatCurrency(phase.invoiced),
    variance: formatCurrency(phase.variance),
  }));

  return (
    <div className="space-y-6">
      {/* Contract Value Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <BudgetCard
          title="Contract Value"
          value={formatCurrency(contractValueDisplay.total)}
          variant="default"
        />
        <BudgetCard
          title="Invoiced"
          value={formatCurrency(contractValueDisplay.invoiced)}
          subtitle={formatPercent(contractValueDisplay.invoiced / contractValueDisplay.total)}
          variant="default"
        />
        <BudgetCard
          title="Recognised"
          value={formatCurrency(contractValueDisplay.recognised)}
          subtitle={formatPercent(contractValueDisplay.recognised / contractValueDisplay.total)}
          variant="default"
        />
        <BudgetCard
          title="Remaining"
          value={formatCurrency(contractValueDisplay.remaining)}
          subtitle={formatPercent(contractValueDisplay.remaining / contractValueDisplay.total)}
          variant="default"
        />
      </div>

      {/* Billing Milestones */}
      <Card>
        <CardHeader>
          <CardTitle>Billing Milestones</CardTitle>
        </CardHeader>
        <CardContent>
          <BudgetTable
            variant="simple"
            columns={[
              { key: 'milestone', label: 'Milestone', align: 'left' },
              { key: 'amount', label: 'Amount', align: 'right' },
              { key: 'dueDate', label: 'Due Date', align: 'left' },
              { key: 'invoiced', label: 'Invoiced', align: 'center' },
              { key: 'paid', label: 'Paid', align: 'center' },
              { key: 'status', label: 'Status', align: 'left' },
            ]}
            data={milestoneRows}
            totalRow={{
              milestone: 'TOTAL',
              amount: formatCurrency(contractValueDisplay.total),
              dueDate: '',
              invoiced: formatCurrency(contractValueDisplay.invoiced),
              paid: formatCurrency(contractValueDisplay.invoiced - (contractValueDisplay.recognised - contractValueDisplay.invoiced)),
              status: '',
            }}
          />
        </CardContent>
      </Card>

      {/* Revenue Recognition */}
      <Card>
        <CardHeader>
          <CardTitle>Revenue Recognition</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <span className="font-medium">Method:</span>
              <span>{revenueRecognition.method}</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">% Complete (by cost):</span>
                  <span className="font-medium">{formatPercent(revenueRecognition.percentComplete)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Revenue to Recognise:</span>
                  <span className="font-medium">{formatCurrency(revenueRecognition.toRecognise)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Revenue Recognised:</span>
                  <span className="font-medium">{formatCurrency(revenueRecognition.recognised)}</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Deferred Revenue:</span>
                  <span className="font-medium text-amber-600">
                    {formatCurrency(revenueRecognition.deferred)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Unbilled Revenue:</span>
                  <span className="font-medium text-blue-600">
                    {formatCurrency(revenueRecognition.unbilled)}
                  </span>
                </div>
              </div>
            </div>

            <div className="text-xs text-muted-foreground pt-2 border-t">
              <p><strong>Deferred Revenue:</strong> Invoiced but not yet earned</p>
              <p><strong>Unbilled Revenue:</strong> Earned but not yet invoiced</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Revenue by Phase */}
      <Card>
        <CardHeader>
          <CardTitle>Revenue by Phase</CardTitle>
        </CardHeader>
        <CardContent>
          <BudgetTable
            variant="simple"
            columns={[
              { key: 'phase', label: 'Phase', align: 'left' },
              { key: 'allocated', label: 'Allocated', align: 'right' },
              { key: 'recognised', label: 'Recognised', align: 'right' },
              { key: 'invoiced', label: 'Invoiced', align: 'right' },
              { key: 'variance', label: 'Variance', align: 'right' },
            ]}
            data={phaseRows}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// Time & Materials Project View
function TimeAndMaterialsSalesView({ data }: { data: SalesBudget }) {
  const { estimated, actual, invoiced, nteAmount, streams, unbilledTime, unbilledExpenses } = data;

  if (!estimated || !streams) {
    return <div>Incomplete data for T&M project</div>;
  }

  // Create an estimated object for display
  const estimatedDisplay = {
    total: estimated,
    actual: actual || 0,
    invoiced: invoiced || 0,
    nte: nteAmount || 0,
  };

  const unbilledTotal = (unbilledTime || 0) + (unbilledExpenses || 0);

  const streamRows = streams.map((stream) => ({
    id: stream.id,
    stream: stream.name,
    estimated: formatCurrency(stream.estimated),
    actual: formatCurrency(stream.actual),
    invoiced: formatCurrency(stream.invoiced),
    remaining: formatCurrency(stream.remaining),
    children: stream.subcategories?.map((sub) => ({
      id: sub.id,
      stream: sub.name,
      estimated: formatCurrency(sub.estimated),
      actual: formatCurrency(sub.actual),
      invoiced: formatCurrency(sub.invoiced),
      remaining: formatCurrency(sub.remaining),
    })),
  }));

  return (
    <div className="space-y-6">
      {/* Revenue Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <BudgetCard
          title="Estimated"
          value={formatCurrency(estimatedDisplay.total)}
          variant="default"
        />
        <BudgetCard
          title="Actual"
          value={formatCurrency(estimatedDisplay.actual)}
          subtitle={formatPercent(estimatedDisplay.actual / estimatedDisplay.total)}
          variant="default"
        />
        <BudgetCard
          title="Invoiced"
          value={formatCurrency(estimatedDisplay.invoiced)}
          subtitle={formatPercent(estimatedDisplay.invoiced / estimatedDisplay.total)}
          variant="default"
        />
        <BudgetCard
          title="NTE Cap"
          value={formatCurrency(estimatedDisplay.nte || 0)}
          subtitle="Not to Exceed"
          variant="default"
        />
      </div>

      {/* Revenue by Stream */}
      <Card>
        <CardHeader>
          <CardTitle>Revenue by Stream</CardTitle>
        </CardHeader>
        <CardContent>
          <BudgetTable
            variant="expandable"
            columns={[
              { key: 'stream', label: 'Revenue Stream', align: 'left' },
              { key: 'estimated', label: 'Estimated', align: 'right' },
              { key: 'actual', label: 'Actual', align: 'right' },
              { key: 'invoiced', label: 'Invoiced', align: 'right' },
              { key: 'remaining', label: 'Remaining', align: 'right' },
            ]}
            data={streamRows}
            totalRow={{
              stream: 'TOTAL',
              estimated: formatCurrency(estimatedDisplay.total),
              actual: formatCurrency(estimatedDisplay.actual),
              invoiced: formatCurrency(estimatedDisplay.invoiced),
              remaining: formatCurrency(estimatedDisplay.total - estimatedDisplay.actual),
            }}
          />
        </CardContent>
      </Card>

      {/* Unbilled Time & Expenses */}
      <Card>
        <CardHeader>
          <CardTitle>Unbilled Time & Expenses</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Ready to Invoice:</p>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span>Billable Time (approved):</span>
                <span className="font-medium">{formatCurrency(unbilledTime || 0)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Expenses (approved):</span>
                <span className="font-medium">{formatCurrency(unbilledExpenses || 0)}</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t font-bold">
                <span>TOTAL:</span>
                <span>{formatCurrency(unbilledTotal)}</span>
              </div>
            </div>

            <Button className="w-full">
              <FileText className="mr-2 h-4 w-4" />
              Create Invoice
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Retainer Project View
function RetainerSalesView({ data }: { data: SalesBudget }) {
  const { retainer, utilisation, monthlyBreakdown } = data;

  if (!retainer || !utilisation || !monthlyBreakdown) {
    return <div>Incomplete data for retainer project</div>;
  }

  const monthlyRows = monthlyBreakdown.map((month) => ({
    id: month.month,
    month: month.month,
    retainer: formatCurrency(month.retainerAmount),
    hoursIncluded: formatHours(month.hoursIncluded),
    hoursUsed: formatHours(month.hoursUsed),
    overage: month.overageAmount > 0 ? formatCurrency(month.overageAmount) : '-',
    total: formatCurrency(month.totalBilled),
  }));

  // Calculate YTD totals
  const ytdTotals = monthlyBreakdown.reduce(
    (acc, month) => ({
      retainer: acc.retainer + month.retainerAmount,
      hoursIncluded: acc.hoursIncluded + month.hoursIncluded,
      hoursUsed: acc.hoursUsed + month.hoursUsed,
      overage: acc.overage + month.overageAmount,
      total: acc.total + month.totalBilled,
    }),
    { retainer: 0, hoursIncluded: 0, hoursUsed: 0, overage: 0, total: 0 }
  );

  return (
    <div className="space-y-6">
      {/* Retainer Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <BudgetCard
          title="Monthly Retainer"
          value={formatCurrency(retainer.monthlyAmount)}
          subtitle="/month"
          variant="default"
        />
        <BudgetCard
          title="Contract Value"
          value={formatCurrency(retainer.contractValue)}
          subtitle={`${retainer.contractMonths} months`}
          variant="default"
        />
        <BudgetCard
          title="Invoiced"
          value={formatCurrency(retainer.invoiced)}
          subtitle={`${retainer.monthsCompleted} months`}
          variant="default"
        />
        <BudgetCard
          title="Remaining"
          value={formatCurrency(retainer.remaining)}
          subtitle={`${retainer.monthsRemaining} months`}
          variant="default"
        />
      </div>

      {/* Hours Utilisation - Current Month */}
      <Card>
        <CardHeader>
          <CardTitle>Hours Utilisation (Current Month: {utilisation.currentMonth})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Included Hours:</div>
                <div className="text-2xl font-bold">{formatHours(utilisation.includedHours)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Hours Used:</div>
                <div className="text-2xl font-bold">{formatHours(utilisation.hoursUsed)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Hours Remaining:</div>
                <div className="text-2xl font-bold">{formatHours(utilisation.hoursRemaining)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Overage Rate:</div>
                <div className="text-2xl font-bold">{formatCurrency(utilisation.overageRate)}/hr</div>
              </div>
            </div>

            <BudgetProgressBar
              value={utilisation.percentUsed}
              max={100}
              variant="default"
              showLabel
              label={`${formatPercent(utilisation.percentUsed)} used`}
            />

            <div className="flex items-center gap-4 text-sm">
              <span className="text-muted-foreground">Rollover Policy:</span>
              <div className="flex gap-2">
                <Badge variant={utilisation.rolloverPolicy === 'none' ? 'default' : 'outline'}>
                  No rollover
                </Badge>
                <Badge variant={utilisation.rolloverPolicy === 'limited' ? 'default' : 'outline'}>
                  Max {utilisation.rolloverHours} hrs
                </Badge>
                <Badge variant={utilisation.rolloverPolicy === 'unlimited' ? 'default' : 'outline'}>
                  Unlimited
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <BudgetTable
            variant="simple"
            columns={[
              { key: 'month', label: 'Month', align: 'left' },
              { key: 'retainer', label: 'Retainer', align: 'right' },
              { key: 'hoursIncluded', label: 'Hours Incl', align: 'right' },
              { key: 'hoursUsed', label: 'Hours Used', align: 'right' },
              { key: 'overage', label: 'Overage', align: 'right' },
              { key: 'total', label: 'Total', align: 'right' },
            ]}
            data={monthlyRows}
            totalRow={{
              month: 'YTD Total',
              retainer: formatCurrency(ytdTotals.retainer),
              hoursIncluded: formatHours(ytdTotals.hoursIncluded),
              hoursUsed: formatHours(ytdTotals.hoursUsed),
              overage: formatCurrency(ytdTotals.overage),
              total: formatCurrency(ytdTotals.total),
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
